import useJumboHeaderTheme from "@jumbo/hooks/useJumboHeaderTheme";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import { SIDEBAR_STYLES, SIDEBAR_VARIANTS } from "@jumbo/utils/constants";
import { HomeOutlined } from "@mui/icons-material";
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import { Button, Chip, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useMediaQuery } from "@mui/material";
import Stack from "@mui/material/Stack";
import { getUserFullName, isRoleCheckList } from 'app/utils/appHelpers';
import { fontSettings } from 'config';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutUser, reset } from 'stores/auth';
import { setFontSettings } from 'stores/meeting';
import Logo from "../../../../shared/Logo";
import AuthUserDropdown from "../../../../shared/widgets/AuthUserDropdown";


const Header = () => {
    const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();
    const [dropdownSearchVisibility, setDropdownSearchVisibility] = React.useState(false);
    const { headerTheme } = useJumboHeaderTheme();
    const dispatch = useDispatch();
    const currentFontSettings = useSelector((state) => state.meeting.fontSettings);
    const authUser = useSelector((state) => state.auth.authUser);
    const allAuthorizedInsts = useSelector(state => state.auth.allAuthorizedInsts);
    const { announcements, announcementsLoading } = useSelector((state) => state.general);
    const navigate = useNavigate();



    // Kullanıcının görebileceği duyuru sayısını hesapla
    const getVisibleAnnouncementsCount = () => {
        if (!announcements || announcements.length === 0) return 0;

        let visibleAnnouncements = announcements.filter(x => x.status === 1);

        if (allAuthorizedInsts && allAuthorizedInsts.length > 0) {
            visibleAnnouncements = visibleAnnouncements.filter(x => {
                const toAnnouncementArray = x.toAnnouncement ? x.toAnnouncement.split(',').map(item => item.trim()) : [];
                return toAnnouncementArray.some(inst => allAuthorizedInsts.includes(inst));
            });
        }

        return visibleAnnouncements.length;
    };

    const handleFontSizeChange = (event, newSize) => {
        if (newSize !== null) {
            const selectedSettings = fontSettings.find(setting => setting.id === newSize);
            if (selectedSettings) {
                const newSettings = {
                    baseFontSize: selectedSettings.baseFontSize,
                    avatarSize: selectedSettings.avatarSize,
                    iconFontSize: selectedSettings.iconFontSize,
                    meetingTitle: selectedSettings.meetingTitle,
                    itemSize: selectedSettings.itemSize,
                    agendaFontSize: selectedSettings.agendaFontSize,
                    titleFontSize: selectedSettings.titleFontSize,
                    attechmentTableFontSize: selectedSettings.attechmentTableFontSize,
                    lineHeight: selectedSettings.lineHeight,
                    buttonSpacing: selectedSettings.buttonSpacing
                };
                dispatch(setFontSettings(newSettings));
            }
        }
    };

    const onLogout = () => {
        dispatch(logoutUser());
        dispatch(reset());
    };

    const getCurrentSize = () => {
        const fontSize = currentFontSettings?.baseFontSize;
        if (fontSize === "10px") return 'small';
        if (fontSize === "12px") return 'medium';
        if (fontSize === "14px") return 'large';
        return 'medium'; // default
    };

    const showDropdownSearch = useMediaQuery('(max-width:575px)');

    const getDetailSearchClick = () => {
        navigate('/detailSearchPage');
    };

    const handleAnnouncementsClick = () => {
        navigate('/listAnnouncement');
    };
    return (
        <React.Fragment>
            {
                (
                    sidebarOptions.style === SIDEBAR_STYLES.CLIPPED_UNDER_HEADER
                    || sidebarOptions.variant === SIDEBAR_VARIANTS.TEMPORARY
                    || (sidebarOptions.variant === SIDEBAR_VARIANTS.PERSISTENT && !sidebarOptions.open)
                ) &&
                <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="open drawer"
                    sx={{
                        ml: sidebarOptions.style === SIDEBAR_STYLES.CLIPPED_UNDER_HEADER ? -2 : 0,
                        mr: 3,
                    }}
                    onClick={() => setSidebarOptions({ open: !sidebarOptions.open })}
                >
                    {
                        sidebarOptions?.open ? <MenuOpenIcon /> : <MenuIcon />
                    }
                </IconButton>
            }
            {
                sidebarOptions?.style === SIDEBAR_STYLES.CLIPPED_UNDER_HEADER &&
                <Logo sx={{ mr: 3 }} mode={headerTheme.type ?? "light"} />
            }

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                {/* En soldaki Hoş Geldiniz mesajı */}
                {authUser !== null && authUser && (
                    <Typography variant='h5' sx={{ fontWeight: 450, marginLeft: "-25px", marginTop: "50px" }}>
                        Sayın {getUserFullName(authUser)}
                    </Typography>
                )}

                {/* Sağ taraftaki öğeler */}
                <Stack direction="row" alignItems="center" spacing={1.25}>
                    {/* Duyuru İkonu - Sadece belirli roller görebilir */}
                    {authUser !== null && authUser &&
                        authUser.userRoles &&
                        authUser.userRoles.some(role => [1, 370, 372, 373, 369].includes(role.id)) && (
                            <Tooltip title="Duyurular" placement="top">
                                <IconButton
                                    onClick={handleAnnouncementsClick}
                                    sx={{
                                        position: 'relative',
                                        cursor: 'pointer',
                                        minWidth: 'auto',
                                        padding: '8px'
                                    }}
                                >
                                    <NotificationsIcon fontSize="medium" color="primary" />
                                    {getVisibleAnnouncementsCount() > 0 && (
                                        <Chip
                                            label={getVisibleAnnouncementsCount()}
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: '-5px',
                                                right: '-5px',
                                                height: '18px',
                                                minWidth: '18px',
                                                fontSize: '10px',
                                                backgroundColor: '#ff4444',
                                                color: 'white',
                                                '& .MuiChip-label': {
                                                    padding: '0 4px'
                                                }
                                            }}
                                        />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}

                    {authUser !== null && (isRoleCheckList(authUser.userRoles, [1, 373, 368, 369, 370, 371, 372, 418, 375])) ? (
                        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => getDetailSearchClick()}>

                            <Button

                                variant="outlined"
                                color="primary"
                                sx={{
                                    marginLeft: '8px',
                                    textTransform: 'none',
                                    padding: '4px 8px'
                                }}
                            >
                                <SearchIcon fontSize={"small"} /> Detaylı Arama
                            </Button>
                        </div>
                    ) : null}

                    <ToggleButtonGroup
                        value={getCurrentSize()}
                        exclusive
                        onChange={handleFontSizeChange}
                        aria-label="font size"
                        size="small"
                        sx={{ height: '28px' }}
                    >
                        <ToggleButton
                            variant={getCurrentSize() === 'small' ? 'contained' : 'outlined'}
                            sx={{
                                borderRadius: '8px',
                                minWidth: '28px',
                                padding: '4px'
                            }}
                            value="small"
                        >
                            <Tooltip title="Küçük Font">
                                <span style={{ fontSize: '10px' }}>A</span>
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton
                            variant={getCurrentSize() === 'medium' ? 'contained' : 'outlined'}
                            sx={{
                                borderRadius: '8px',
                                minWidth: '28px',
                                padding: '4px'
                            }}
                            value="medium"
                        >
                            <Tooltip title="Orta Font">
                                <span style={{ fontSize: '12px' }}>A</span>
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton
                            variant={getCurrentSize() === 'large' ? 'contained' : 'outlined'}
                            sx={{
                                borderRadius: '8px',
                                minWidth: '28px',
                                padding: '4px'
                            }}
                            value="large"
                        >
                            <Tooltip title="Büyük Font">
                                <span style={{ fontSize: '14px' }}>A</span>
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <AuthUserDropdown />

                    <Tooltip title="Ana Sayfa" placement="top">
                        <Button
                            onClick={() => navigate("/")}
                            sx={{
                                cursor: 'pointer',
                                minWidth: 'auto',
                                padding: '8px'
                            }}
                        >
                            <HomeOutlined fontSize="medium" color="primary" />
                        </Button>
                    </Tooltip>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={onLogout}
                        startIcon={<LogoutIcon />}
                    >
                        Çıkış Yap (BBS)
                    </Button>
                </Stack>
            </Stack>
        </React.Fragment >
    );
};

export default Header;
